import { Document, Image, Text, View } from '@react-pdf/renderer'
import {
  ELIGIBILITY_LABELS,
  ELIGIBILITY_STATUS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import {
  sanitizeForPdf,
  stripLeadingBullet,
} from 'features/reporting/pdfGeneration/utils/formatters'
import React from 'react'

import { ELIGIBILITY_PDF_COLORS, NiiPdfStyles } from './NiiReportPdfStyles'

export interface NiiReportPdfZone {
  title: string
  color: string
  zone_recommendations: ReadonlyArray<string>
}

export interface NiiPdfZoneImage {
  name: string
  dataUrl: string
}

export interface NiiReportPdfProps {
  siteName?: string
  assetName?: string
  analysisDate?: string
  analysisVersion: number
  executiveSummary?: string
  internalComments?: string
  eligibility: EligibilityStatus
  recommendations: ReadonlyArray<string>
  justification?: string
  zones: ReadonlyArray<NiiReportPdfZone>
  zoneImages: ReadonlyArray<NiiPdfZoneImage>
}

const SectionCard: React.FC<{
  title: string
  children: React.ReactNode
}> = ({ title, children }) => (
  <View style={NiiPdfStyles.sectionCard} wrap={false}>
    <Text style={NiiPdfStyles.sectionCardTitle}>{title}</Text>
    <View style={NiiPdfStyles.sectionCardBody}>{children}</View>
  </View>
)

const DescriptionRow: React.FC<{
  label: string
  children: React.ReactNode
}> = ({ label, children }) => (
  <View style={NiiPdfStyles.descriptionRow}>
    <Text style={NiiPdfStyles.descriptionLabel}>{label}</Text>
    <View style={NiiPdfStyles.descriptionValue}>{children}</View>
  </View>
)

const EligibilityTag: React.FC<{ status: EligibilityStatus }> = ({
  status,
}) => {
  const bgColor =
    ELIGIBILITY_PDF_COLORS[status] ?? ELIGIBILITY_PDF_COLORS.unclear
  return (
    <View style={[NiiPdfStyles.eligibilityTag, { backgroundColor: bgColor }]}>
      <Text style={NiiPdfStyles.eligibilityTagText}>
        {ELIGIBILITY_LABELS[status] ?? 'Unknown'}
      </Text>
    </View>
  )
}

const RecommendationsList: React.FC<{
  items: ReadonlyArray<string>
}> = ({ items }) => (
  <View>
    {items.map((item, i) => (
      <View key={i} style={NiiPdfStyles.recommendationItem}>
        <Text style={NiiPdfStyles.recommendationBullet}>•</Text>
        <Text style={NiiPdfStyles.recommendationText}>
          {sanitizeForPdf(stripLeadingBullet(item))}
        </Text>
      </View>
    ))}
  </View>
)

export const NiiReportPdfDocument: React.FC<NiiReportPdfProps> = ({
  siteName,
  assetName,
  analysisDate,
  analysisVersion,
  executiveSummary,
  internalComments,
  eligibility,
  recommendations,
  justification,
  zones,
  zoneImages,
}) => {
  const zonesWithRecommendations = zones.filter(
    (z) => z.zone_recommendations.length > 0
  )

  return (
    <Document>
      <ReportPage
        header={<PageHeader title='NII Report' />}
        footer={<PageFooter />}>
        <SectionCard title='Basic Information'>
          {siteName && (
            <DescriptionRow label='Site'>
              <Text style={NiiPdfStyles.preformattedText}>{siteName}</Text>
            </DescriptionRow>
          )}
          {assetName && (
            <DescriptionRow label='Asset'>
              <Text style={NiiPdfStyles.preformattedText}>{assetName}</Text>
            </DescriptionRow>
          )}
          <DescriptionRow label='Analysis Date'>
            <Text style={NiiPdfStyles.preformattedText}>
              {analysisDate ?? '—'}
            </Text>
          </DescriptionRow>
          <DescriptionRow label='Analysis Version'>
            <Text style={NiiPdfStyles.preformattedText}>
              v{analysisVersion}
            </Text>
          </DescriptionRow>
        </SectionCard>

        {executiveSummary && (
          <SectionCard title='Executive Summary'>
            <Text style={NiiPdfStyles.preformattedText}>
              {sanitizeForPdf(executiveSummary)}
            </Text>
          </SectionCard>
        )}

        {internalComments && (
          <SectionCard title='Internal Assessment Comments'>
            <Text style={NiiPdfStyles.preformattedText}>
              {sanitizeForPdf(internalComments)}
            </Text>
          </SectionCard>
        )}

        <SectionCard title='Conclusion'>
          <DescriptionRow label='Eligibility'>
            <EligibilityTag
              status={eligibility ?? ELIGIBILITY_STATUS.UNCLEAR}
            />
          </DescriptionRow>
          {recommendations.length > 0 && (
            <DescriptionRow label='Recommendations'>
              <RecommendationsList items={recommendations} />
            </DescriptionRow>
          )}
          {justification && (
            <DescriptionRow label='Justification'>
              <Text style={NiiPdfStyles.preformattedText}>
                {sanitizeForPdf(justification)}
              </Text>
            </DescriptionRow>
          )}
        </SectionCard>

        {zoneImages.length > 0 && (
          <SectionCard title='Zone Reference Images'>
            {zoneImages.map((img, i) => (
              <View key={i} style={NiiPdfStyles.zoneImageBlock}>
                <Text style={NiiPdfStyles.zoneImageLabel}>{img.name}</Text>
                <Image src={img.dataUrl} style={NiiPdfStyles.zoneImage} />
              </View>
            ))}
          </SectionCard>
        )}

        {zonesWithRecommendations.length > 0 && (
          <SectionCard title='Zone-Specific Recommendations'>
            {zonesWithRecommendations.map((zone, i) => (
              <View key={i} style={NiiPdfStyles.zoneBlock}>
                <View style={NiiPdfStyles.zoneHeading}>
                  <View
                    style={[
                      NiiPdfStyles.zoneColorDot,
                      { backgroundColor: zone.color },
                    ]}
                  />
                  <Text style={NiiPdfStyles.zoneTitle}>{zone.title}</Text>
                </View>
                <RecommendationsList items={zone.zone_recommendations} />
              </View>
            ))}
          </SectionCard>
        )}
      </ReportPage>
    </Document>
  )
}
